import {
  DocumentTemplate,
  EntityMeta,
  TEMPLATE_TYPE_FROM_API_VALUE,
} from '@qtek/shared/models';

export function withTemplateType(res: DocumentTemplate): DocumentTemplate {
  const apiType = (res as unknown as { type?: string }).type;
  return apiType ? { ...res, tp: TEMPLATE_TYPE_FROM_API_VALUE[apiType] } : res;
}

/**
 * The WS `ins` (create) ack returns a Template without the `_cts`/`_uts`
 * timestamps the list renders, which made a brand-new row display a bare "on"
 * with no date in the Templates list. Backfill the client-first-write values
 * here — the authoritative record replaces them on the next query/upd, this
 * just keeps the create ack row consistent with the queried ones.
 */
function withInsertAudit(res: DocumentTemplate): DocumentTemplate {
  const now = new Date().toISOString();
  return {
    ...res,
    _cts: res._cts || now,
    _uts: res._uts || now,
  };
}

export interface TemplatesState {
  templates: DocumentTemplate[];
  selectableTemplates: DocumentTemplate[];
  templatesMeta: EntityMeta | undefined;
  templatesIsLoading: boolean;
}

export const templatesInitialState: TemplatesState = {
  templates: [],
  selectableTemplates: [],
  templatesMeta: undefined,
  templatesIsLoading: false,
};

export function applySelectableTemplatesQuery(
  res: DocumentTemplate[] | undefined
): Partial<TemplatesState> {
  return {
    selectableTemplates: (res || []).map(withTemplateType),
  };
}

export function applyTemplatesQuery(
  res: DocumentTemplate[] | undefined,
  meta: EntityMeta | undefined
): Partial<TemplatesState> {
  return {
    templates: (res || []).map(withTemplateType),
    templatesMeta: meta,
    templatesIsLoading: false,
  };
}

export function applySelectableTemplateInsertSuccess(
  state: TemplatesState,
  res: DocumentTemplate
): Partial<TemplatesState> {
  // Every mounted qt-template-selector holds its own connectSelectable subscription,
  // and each receives the same broadcast — append only once per template id.
  if (state.selectableTemplates.some(template => template._id === res._id)) {
    return {};
  }
  return {
    selectableTemplates: [
      ...state.selectableTemplates,
      withTemplateType(withInsertAudit(res)),
    ],
  };
}

export function applySelectableTemplateUpdateSuccess(
  state: TemplatesState,
  res: DocumentTemplate
): Partial<TemplatesState> {
  return {
    selectableTemplates: state.selectableTemplates.map(template =>
      template._id === res._id ? withTemplateType(res) : template
    ),
  };
}

export function applySelectableTemplateDeleteSuccess(
  state: TemplatesState,
  templateId: string
): Partial<TemplatesState> {
  return {
    selectableTemplates: state.selectableTemplates.filter(
      template => template._id !== templateId
    ),
  };
}

export function applyTemplateInsertSuccess(
  state: TemplatesState,
  res: DocumentTemplate
): Partial<TemplatesState> {
  return {
    templates: [...state.templates, withTemplateType(withInsertAudit(res))],
    templatesIsLoading: false,
  };
}

export function applyTemplateUpdateSuccess(
  state: TemplatesState,
  res: DocumentTemplate
): Partial<TemplatesState> {
  return {
    templates: state.templates.map(template =>
      template._id === res._id ? withTemplateType(res) : template
    ),
    templatesIsLoading: false,
  };
}

export function applyTemplateDeleteSuccess(
  state: TemplatesState,
  templateId: string
): Partial<TemplatesState> {
  return {
    templates: state.templates.filter(template => template._id !== templateId),
    templatesIsLoading: false,
  };
}
